import { getPaginatedCorporates } from '../../../api/corporateApi'
import YesOrNoTag from '../../../components/common/YesOrNoTag'
import CorporatesFilter from '../../../components/corporates/corporates-filter/corporates-filter'
import LinkButton from '../../../components/link-button/link-button'
import { getSearchParams } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import {
  ICorporate,
  ICorporateReport,
  ICorporatesFilterParams,
  IPaginatedCorporatesParams,
} from '../share-type'
import { PlusOutlined, ExportOutlined } from '@ant-design/icons'
import { Button, Col, Row, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const Corporates = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [corporates, setCorporates] = useState<(ICorporate & ICorporateReport)[]>([])
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [searchValues, setSearchValues] = useState<ICorporatesFilterParams>()

  useEffect(() => {
    const searchName = searchParams.get('name')
    const searchIsActive = searchParams.get('isActive')
    const searchPhoneNumber = searchParams.get('phoneNumber')
    const searchUserId = searchParams.get('userId')
    const searchPageSize = searchParams.get('size')
    const searchPage = searchParams.get('page')
    const newSearchValues = {
      name: searchName ?? undefined,
      isActive: searchIsActive ? (searchIsActive === 'true' ? true : false) : undefined,
      phoneNumber: searchPhoneNumber ?? undefined,
      userId: searchUserId ? Number(searchUserId) : undefined,
    }
    const data = {
      size: searchPageSize === null ? defaultPage.pageSize : Number(searchPageSize),
      page: searchPage === null ? defaultPage.currentPage - 1 : Number(searchPage),
      ...newSearchValues,
    }
    setSearchValues(newSearchValues)
    getPaginatedCorporatesAction(data)
  }, [])

  const getPaginatedCorporatesAction = (data: IPaginatedCorporatesParams) => {
    getPaginatedCorporates(data).then(res => {
      if (res && res.pageInfo && res.data) {
        setPageInfo({
          ...res.pageInfo,
          currentPage: res.pageInfo.currentPage + 1,
        })
        setCorporates(
          res.data.map(value => ({
            ...value.corporate,
            ...value.report,
          })),
        )
      }
    })
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    const data = {
      size: pageSize,
      page: page - 1,
      ...searchValues,
    }
    getPaginatedCorporatesAction(data)
  }
  const onSearch = (value: ICorporatesFilterParams) => {
    const data = {
      size: pageInfo.pageSize,
      page: 0,
      ...value,
    }
    setSearchValues(value)
    setSearchParams(getSearchParams(value))
    getPaginatedCorporatesAction(data)
  }

  const columns: ColumnsType<ICorporate & ICorporateReport> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: translate('name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: translate('isActive'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: number) => <YesOrNoTag value={isActive} />,
    },
    {
      title: translate('corporate.principal'),
      dataIndex: 'principal',
      key: 'principal',
    },
    {
      title: translate('corporate.cooperationMode'),
      dataIndex: 'cooperationMode',
      key: 'cooperationMode',
    },
    {
      title: translate('corporate.contractDeadline'),
      dataIndex: 'contractDeadline',
      key: 'contractDeadline',
    },
    {
      title: translate('corporate.activeMemberNumber'),
      dataIndex: 'activatedMemberCount',
      key: 'activatedMemberCount',
    },
    {
      title: translate('corporate.credits'),
      dataIndex: 'credits',
      key: 'credits',
    },
    {
      title: translate('corporate.price'),
      dataIndex: 'price',
      key: 'price',
      render: (price: number | null) => (price === null ? '-' : (price / 100).toFixed(2)),
    },
    {
      title: translate('corporate.unusedDistributedCredits'),
      dataIndex: 'unusedDistributedCredits',
      key: 'unusedDistributedCredits',
    },
    {
      title: translate('corporate.usageRate'),
      key: 'usageRate',
      render: (text: string, record: ICorporate & ICorporateReport) =>
        ((record.memberUsedCredits * 100.0) / record.distributedCredits || 0).toFixed(2) + '%',
    },
    {
      title: translate('corporate.nextDistributionTime'),
      dataIndex: 'nextDistributionTime',
      key: 'nextDistributionTime',
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      render: (text: string, record: ICorporate) => (
        <Space>
          <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
            <LinkButton to={`/corporates/${record.id}/edit`}>编辑</LinkButton>
          </AuthPermission>

          <LinkButton to={`/corporates/${record.id}`}>查看</LinkButton>
        </Space>
      ),
    },
  ]

  return (
    <Space className="table-space" orientation="vertical" size={16}>
      <Row align="middle">
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className="left-col">
          <div>合作企业</div>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className="right-col">
          <Space>
            <AuthPermission permission={PermissionsEnum.CORPORATES_WRITE}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/corporates/new')}
              >
                创建新的合作企业
              </Button>
            </AuthPermission>
            <Button
              type="link"
              icon={<ExportOutlined />}
              onClick={() =>
                window.open('https://metabase.guavapass.cn/question/1504-corporate-overview')
              }
            >
              导出
            </Button>
          </Space>
        </Col>
      </Row>
      <Row className="filter-bar">
        <CorporatesFilter onSearch={onSearch} formData={searchValues} />
      </Row>

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={corporates}
        pagination={{
          current: pageInfo.currentPage,
          pageSize: pageInfo.pageSize,
          total: pageInfo.count,
          onChange: paginationOnChange,
        }}
      />
    </Space>
  )
}

export default Corporates
